import { useEffect, useState, useMemo } from 'react';
import VectorSource from 'ol/source/Vector';

import { useProject } from '@contexts/Project.context';
import { useMapLayer } from '@hooks/useMapLayer';
import { useContoursData } from './hooks/useContoursData';
import { createContourStyleFunction } from '@utils/map/styles/contoursStyles';
import {
	LAYER_ID,
	LAYER_NAME,
	SIDEBAR_ID,
	INTERVAL_OPTIONS,
	INTERVAL_KEYS,
	DEFAULT_CONTOUR_COLOR,
} from '@config/contoursLayerConfig';
import LayerSettings from '../../sidebars/sidebarElements/LayerSettings';
import ContoursSettings from './ContoursSettings';
import { useToast } from '@contexts/Toast.context';

/**
 * Contours GeoJSON Layer Component
 * Displays elevation contour lines on the map with configurable detail levels and styling
 *
 * @param {Object} contoursConfig - Configuration object containing file paths and styling
 * @param {boolean} visible - Initial visibility state
 * @param {number} zIndex - Layer z-index for stacking order
 * @param {Function} setContoursSettingsActive - Callback to activate settings sidebar
 */
const ContoursLayer = ({
	contoursConfig,
	visible = false,
	zIndex = 36,
	setContoursSettingsActive,
}) => {
	const { addToast } = useToast();
	const { mapObject, project, defaultFormat } = useProject();
	const [selectedInterval, setSelectedInterval] = useState(INTERVAL_KEYS.MEDIUM);
	const [isLayerVisible, setIsLayerVisible] = useState(visible);
	const [lineColor, setLineColor] = useState(DEFAULT_CONTOUR_COLOR);
	const { uuid } = project || {};

	// Filter available intervals based on config
	const availableIntervals = useMemo(() => {
		if (!contoursConfig?.files) return [];
		return INTERVAL_OPTIONS.filter(opt => contoursConfig.files[opt.key]);
	}, [contoursConfig]);

	// Get current file path and interval
	const currentFilePath = useMemo(() => {
		if (!contoursConfig?.files?.[selectedInterval]) return null;
		return contoursConfig.files[selectedInterval].path;
	}, [contoursConfig, selectedInterval]);

	const currentIntervalMeters = useMemo(() => {
		if (!contoursConfig?.files?.[selectedInterval]) return 1;
		return contoursConfig.files[selectedInterval].interval_meters || 1;
	}, [contoursConfig, selectedInterval]);

	// Create style function
	const styleFunction = useMemo(() => {
		return createContourStyleFunction(
			contoursConfig?.styling,
			currentIntervalMeters,
			lineColor
		);
	}, [contoursConfig?.styling, currentIntervalMeters, lineColor]);

	// Initialize map layer
	const { layer } = useMapLayer(mapObject, LAYER_ID, {
		name: LAYER_NAME,
		zIndex,
		visible: isLayerVisible,
		styleFunction,
		declutter: true,
	});

	// Fetch contours data
	const {
		data: contoursData,
		error,
		isFetching,
	} = useContoursData(uuid, currentFilePath, isLayerVisible);

	// Load features into layer when data is available
	useEffect(() => {
		if (!layer || !contoursData || error) return;

		try {
			// console.log('contoursData: ', contoursData);
			const crsName = contoursData?.crs?.properties?.name || '';
			const isEpsg3857 = crsName.includes('3857');

			const features = defaultFormat.readFeatures(contoursData, {
				dataProjection: isEpsg3857 ? 'EPSG:3857' : 'EPSG:4326',
				featureProjection: 'EPSG:3857',
			});

			const source = new VectorSource({ features });
			layer.setSource(source);
		} catch (err) {
			console.error('[Contours] Error loading features:', err);
			addToast({
				id: 'contours-error',
				title: 'Failed to load contours.',
				bg: 'danger',
			});
		}
	}, [layer, contoursData, error, defaultFormat, addToast]);

	// Handle visibility changes
	const handleVisibilityChange = newVisible => {
		setIsLayerVisible(newVisible);
	};

	// Don't render if no layer or no available intervals
	if (!layer || availableIntervals.length === 0) return null;

	return (
		<li>
			<LayerSettings
				id={LAYER_ID}
				layer={layer}
				label={LAYER_NAME}
				defaultState={visible}
				onCheck={() => handleVisibilityChange(true)}
				onUncheck={() => handleVisibilityChange(false)}
				disabled={!layer}
				layerFunctions={[
					{
						title: 'Settings',
						onClick: setContoursSettingsActive,
					},
				]}
			/>
			<ContoursSettings
				sidebarId={SIDEBAR_ID}
				lineColor={lineColor}
				setLineColor={setLineColor}
				selectedInterval={selectedInterval}
				setSelectedInterval={setSelectedInterval}
				availableIntervals={availableIntervals}
				contoursConfig={contoursConfig}
				isFetching={isFetching}
			/>
		</li>
	);
};

export default ContoursLayer;
